/*
 * © 2025 Fannie Mae. All rights reserved.
 * This software contains confidential information and trade secrets of Fannie Mae.
 * Use, disclosure, or reproduction is prohibited without the prior written consent of Fannie Mae.
 */

import { forwardRef, useImperativeHandle, useRef, useState } from 'react';

const ALL_COLORS = ['RED', 'YELLOW', 'GREEN', 'BLUE', 'GREY'];

const COLOR_HEX = {
  RED: '#dc3545',
  YELLOW: '#ffc107',
  GREEN: '#28a745',
  BLUE: '#007bff',
  GREY: '#bbbbbb',
};

// Normalise raw slaColor string to one of the five canonical names, or null.
const getCanonicalColor = (value) => {
  if (!value || typeof value !== 'string') return null;
  const normalized = value.trim().toUpperCase();
  if (normalized === 'RED') return 'RED';
  if (normalized === 'YELLOW' || normalized === 'AMBER') return 'YELLOW';
  if (normalized === 'GREEN') return 'GREEN';
  if (normalized === 'BLUE') return 'BLUE';
  if (normalized === 'GREY' || normalized === 'GRAY') return 'GREY';
  return null;
};

// AG Grid legacy custom filter — five colored-swatch checkboxes for SLA tier filtering.
// Implements IFilterComp methods via forwardRef + useImperativeHandle.
const SlaColorFilter = forwardRef((props, ref) => {
  const [activeColors, setActiveColorsState] = useState([...ALL_COLORS]);
  const activeColorsRef = useRef([...ALL_COLORS]);

  const setActiveColors = (colors) => {
    activeColorsRef.current = colors;
    setActiveColorsState(colors);
  };

  const notifyFilterChanged = (colors) => {
    if (typeof props.onModelChange === 'function') {
      const model = colors.length === ALL_COLORS.length ? null : { activeColors: [...colors] };
      props.onModelChange(model);
      return;
    }

    if (typeof props.filterChangedCallback === 'function') {
      props.filterChangedCallback();
    }
  };

  useImperativeHandle(ref, () => ({
    isFilterActive() {
      return activeColorsRef.current.length < ALL_COLORS.length;
    },

    doesFilterPass(params) {
      const slaColor = params.data?.slaColor;
      if (!slaColor) return true;
      const canonical = getCanonicalColor(slaColor);
      if (!canonical) return true;
      return activeColorsRef.current.includes(canonical);
    },

    getModel() {
      if (activeColorsRef.current.length === ALL_COLORS.length) return null;
      return { activeColors: [...activeColorsRef.current] };
    },

    setModel(model) {
      const colors = model ? (model.activeColors || [...ALL_COLORS]) : [...ALL_COLORS];
      setActiveColors(colors);
      notifyFilterChanged(colors);
    },

    destroy() {},
  }));

  const handleToggle = (color) => {
    const current = activeColorsRef.current;
    const next = current.includes(color)
      ? current.filter((c) => c !== color)
      : [...current, color];
    setActiveColors(next);
    notifyFilterChanged(next);
  };

  const handleSelectAll = () => {
    setActiveColors([...ALL_COLORS]);
    notifyFilterChanged([...ALL_COLORS]);
  };

  const handleClear = () => {
    setActiveColors([]);
    notifyFilterChanged([]);
  };

  const allSelected = activeColors.length === ALL_COLORS.length;
  const noneSelected = activeColors.length === 0;

  return (
    <div className="sla-color-filter">
      <div className="sla-filter-buttons">
        <button
          className="sla-filter-button"
          onClick={handleSelectAll}
          disabled={allSelected}
          title="Select all SLA colors"
        >
          Select All
        </button>
        <button
          className="sla-filter-button"
          onClick={handleClear}
          disabled={noneSelected}
          title="Clear all selections"
        >
          Clear
        </button>
      </div>
      <div className="sla-filter-list">
        {ALL_COLORS.map((color) => (
          <label key={color} className="sla-color-filter__row">
            <input
              type="checkbox"
              checked={activeColors.includes(color)}
              onChange={() => handleToggle(color)}
              className="sla-checkbox"
            />
            <span
              className="sla-swatch"
              style={{ backgroundColor: COLOR_HEX[color] }}
              title={`SLA color: ${color}`}
            />
            <span className="sla-color-label">{color}</span>
          </label>
        ))}
      </div>
    </div>
  );
});

SlaColorFilter.displayName = 'SlaColorFilter';

export default SlaColorFilter;
